import { useState, useEffect, useRef } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';
import api, { API_BASE } from '../api';
import Layout from '../components/Layout';
import { useToast } from '../components/ToastContext';
import { T } from '../styles/theme';
import { useIsMobile } from '../hooks/useIsMobile';
import comprimeImmagine from '../utils/comprimeImmagine';
const CATEGORIES = ['Scarpe', 'Abbigliamento', 'Profumi', 'Tech', 'Borse', 'Carte Pokemon'];
const POKEMON_MERCATI = [
  { value: 'occidentale', label: '🌍 Occidentale (EN/FR/DE/ES/IT/PT)' },
  { value: 'orientale', label: '🌏 Orientale (JP/KR/CT)' },
  { value: 'cinese_s', label: '🇨🇳 Cinese Semplificato' },
];
const LINGUE_OCCIDENTALE = [
  { value: 'EN', label: 'Inglese (EN)' }, { value: 'FR', label: 'Francese (FR)' },
  { value: 'DE', label: 'Tedesco (DE)' }, { value: 'ES', label: 'Spagnolo (ES)' },
  { value: 'IT', label: 'Italiano (IT)' }, { value: 'PT', label: 'Portoghese (PT)' },
];
const LINGUE_ORIENTALE = [
  { value: 'JP', label: 'Giapponese (JP)' }, { value: 'KR', label: 'Coreano (KR)' },
  { value: 'CT', label: 'Cinese Tradizionale (CT)' },
];
const CASE_GRADING = ['PSA', 'BGS / Beckett', 'GRAAD', 'AI Grading'];
const VOTI_GRADING = [1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5, 5.5, 6, 6.5, 7, 7.5, 8, 8.5, 9, 9.5, 10];
const LOADING_TEXTS = ['🔍 Cerco prezzi su eBay...', '🛍️ Analizzo Vinted...', '🛒 Cerco su Google Shopping...', '🔎 Claude Vision identifica...', '📊 Calcolo la domanda...', '💡 Genero i consigli...', '✅ Quasi pronto...'];

function ScoreBadge({ score }) {
  let bg = T.red, label = '🔴';
  if (score >= 7) { bg = T.green; label = '🟢'; }
  else if (score >= 4) { bg = T.amber; label = '🟡'; }
  return (
    <div className="flex items-center gap-3">
      <div className="text-white text-3xl font-black w-16 h-16 rounded-2xl flex items-center justify-center" style={{ backgroundColor: bg }}>{score}</div>
      <div>
        <p className="font-bold text-lg" style={{ color: T.text, fontFamily: T.fontBody }}>{label} Score vendibilita</p>
        <p className="text-sm" style={{ color: T.muted, fontFamily: T.fontBody }}>{score >= 7 ? 'Ottimo prodotto da vendere' : score >= 4 ? 'Vendibilita media' : 'Difficile da vendere'}</p>
      </div>
    </div>
  );
}

function PriceCard({ title, data, color }) {
  if (!data) return null;
  const hasData = (data.min != null && data.min > 0) || (data.max != null && data.max > 0) || (data.media != null && data.media > 0);
  return (
    <div className="card p-4">
      <p className="text-xs font-medium uppercase tracking-wide mb-2" style={{ color, fontFamily: T.fontBody }}>{title}</p>
      {hasData ? (
        <div className="space-y-1 text-sm" style={{ fontFamily: T.fontBody }}>
          <div className="flex justify-between"><span style={{ color: T.muted }}>Min</span><span style={{ color: T.text }}>{'\u20AC'}{(data.min || 0).toFixed(2)}</span></div>
          <div className="flex justify-between"><span style={{ color: T.muted }}>Media</span><span className="font-bold" style={{ color: T.text }}>{'\u20AC'}{(data.media || 0).toFixed(2)}</span></div>
          <div className="flex justify-between"><span style={{ color: T.muted }}>Max</span><span style={{ color: T.text }}>{'\u20AC'}{(data.max || 0).toFixed(2)}</span></div>
        </div>
      ) : (
        <p className="text-xs" style={{ color: T.muted, fontFamily: T.fontBody }}>Dati insufficienti su questa piattaforma</p>
      )}
    </div>
  );
}

export default function AnalisiMercato() {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const toast = useToast();
  const isMobile = useIsMobile();
  const prodottoState = location.state;

  const [mode, setMode] = useState('foto_nome');
  const [categoria, setCategoria] = useState(searchParams.get('categoria') || '');
  const [nome, setNome] = useState(searchParams.get('nome') || '');
  const [files, setFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [prefilled, setPrefilled] = useState(false);
  const [fromProduct, setFromProduct] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [loadingText, setLoadingText] = useState(0);
  const [result, setResult] = useState(null);
  const [storia, setStoria] = useState([]);
  const [savedBanner, setSavedBanner] = useState(null);
  const [storiaPage, setStoriaPage] = useState(1);
  const STORIA_PER_PAGE = 8;
  // Pokemon form
  const [pokNome, setPokNome] = useState('');
  const [pokMercato, setPokMercato] = useState('occidentale');
  const [pokCodiceSet, setPokCodiceSet] = useState('');
  const [pokNumero, setPokNumero] = useState('');
  const [pokLingua, setPokLingua] = useState('EN');
  const [pokGradata, setPokGradata] = useState(false);
  const [pokCasaGrading, setPokCasaGrading] = useState('PSA');
  const [pokVotoGrading, setPokVotoGrading] = useState(9);
  const [pokResult, setPokResult] = useState(null);
  const fileRef = useRef();
  const autoStarted = useRef(false);

  // Analisi con foto dal prodotto — scarica foto e invia all'endpoint
  const avviaAnalisiConFoto = async (nomeProd, categoriaProd, fotoUrls) => {
    setAnalyzing(true); setLoadingText(0); setResult(null);
    try {
      const fd = new FormData();
      fd.append('nome_prodotto', nomeProd || '');
      fd.append('categoria', categoriaProd || '');
      if (fotoUrls && fotoUrls.length > 0) {
        try {
          const fotoUrl = fotoUrls[0];
          const fullUrl = fotoUrl.startsWith('http') ? fotoUrl : `${API_BASE}${fotoUrl}`;
          const response = await fetch(fullUrl);
          const blob = await response.blob();
          const fileName = fotoUrl.split('/').pop() || 'foto.jpg';
          const raw = new File([blob], fileName, { type: blob.type || 'image/jpeg' });
          const file = await comprimeImmagine(raw, 4);
          fd.append('foto', file);
        } catch (e) { console.warn('Foto non allegata:', e); }
      }
      const res = await api.post('/analisi-mercato', fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 60000 });
      setResult(res.data); setSavedBanner(null);
      toast.success('Analisi completata!');
      loadStoria();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore durante l\'analisi');
    } finally { setAnalyzing(false); }
  };

  // Check for state from ProductDetail (navigazione con foto)
  useEffect(() => {
    if (prodottoState?.from_product && !autoStarted.current) {
      autoStarted.current = true;
      if (prodottoState.nome) setNome(prodottoState.nome);
      if (prodottoState.categoria) setCategoria(prodottoState.categoria);
      if (prodottoState.foto_urls?.length > 0) {
        setPreviews((prodottoState.foto_urls || []).map(u => u.startsWith('http') ? u : `${API_BASE}${u}`));
        setMode('foto_nome');
      }
      setFromProduct(true);
      setPrefilled(true);
      // Avvia analisi automatica con foto dopo 500ms
      setTimeout(() => {
        avviaAnalisiConFoto(prodottoState.nome, prodottoState.categoria, prodottoState.foto_urls);
      }, 500);
      return;
    }
    // Fallback: check localStorage prefill
    const raw = localStorage.getItem('analisi_prefill');
    if (raw) {
      try {
        const data = JSON.parse(raw);
        if (data.nome) setNome(data.nome);
        if (data.categoria) setCategoria(data.categoria);
        if (data.foto_urls?.length > 0) {
          setPreviews((data.foto_urls || []).map(u => `${API_BASE}${u}`));
          setMode('foto_nome');
        }
        setPrefilled(true);
      } catch {}
      localStorage.removeItem('analisi_prefill');
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const [quote, setQuote] = useState(null);
  const loadQuote = () => api.get('/api/quote-rimanenti').then((r) => setQuote(r.data || null)).catch(() => {});
  const loadStoria = () => api.get('/analisi/storia').then((r) => setStoria(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  useEffect(() => { loadStoria(); loadQuote(); }, []);

  useEffect(() => {
    if (!analyzing) return;
    const interval = setInterval(() => setLoadingText((p) => (p + 1) % LOADING_TEXTS.length), 2000);
    return () => clearInterval(interval);
  }, [analyzing]);

  const handleFiles = (e) => {
    const selected = Array.from(e.target.files).slice(0, 20);
    setFiles(selected);
    const newPreviews = [];
    selected.forEach((f) => {
      const reader = new FileReader();
      reader.onload = (ev) => { newPreviews.push(ev.target.result); if (newPreviews.length === selected.length) setPreviews([...newPreviews]); };
      reader.readAsDataURL(f);
    });
    setPrefilled(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const dropped = Array.from(e.dataTransfer.files).filter(f => f.type.startsWith('image/')).slice(0, 20);
    if (dropped.length) {
      setFiles(dropped);
      const newPreviews = [];
      dropped.forEach((f) => {
        const reader = new FileReader();
        reader.onload = (ev) => { newPreviews.push(ev.target.result); if (newPreviews.length === dropped.length) setPreviews([...newPreviews]); };
        reader.readAsDataURL(f);
      });
      setPrefilled(false);
    }
  };

  const handleAnalyze = async () => {
    if (mode !== 'foto' && !nome.trim()) { toast.error('Inserisci il nome del prodotto'); return; }
    if (mode !== 'nome' && files.length === 0 && previews.length === 0) { toast.error('Carica almeno una foto'); return; }
    setAnalyzing(true); setLoadingText(0); setResult(null);
    try {
      const fd = new FormData();
      fd.append('nome_prodotto', nome);
      fd.append('categoria', categoria);
      if (files.length > 0 && mode !== 'nome') {
        const compressed = await comprimeImmagine(files[0], 4);
        fd.append('foto', compressed);
      } else if (prefilled && previews.length > 0 && mode !== 'nome') {
        try {
          const resp = await fetch(previews[0]);
          const blob = await resp.blob();
          const raw = new File([blob], 'foto.jpg', { type: 'image/jpeg' });
          const compressed = await comprimeImmagine(raw, 4);
          fd.append('foto', compressed);
        } catch {}
      }
      const res = await api.post('/analisi-mercato', fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 60000 });
      setResult(res.data); setSavedBanner(null);
      toast.success('Analisi completata!');
      loadStoria();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore durante l\'analisi');
    } finally { setAnalyzing(false); }
  };

  const handleAnalyzePokemon = async () => {
    if (!pokNome.trim()) { toast.error('Inserisci il nome della carta'); return; }
    if (!pokCodiceSet.trim()) { toast.error('Inserisci il codice set'); return; }
    if (!pokNumero.trim()) { toast.error('Inserisci il numero carta'); return; }
    setAnalyzing(true); setLoadingText(0); setResult(null); setPokResult(null);
    try {
      const res = await api.post('/analisi/pokemon', {
        nome: pokNome, codice_set: pokCodiceSet, numero: pokNumero,
        mercato: pokMercato, lingua: pokMercato === 'cinese_s' ? 'CS' : pokLingua,
        gradata: pokGradata,
        casa_grading: pokGradata ? pokCasaGrading : null,
        voto_grading: pokGradata ? pokVotoGrading : null,
      }, { timeout: 60000 });
      setPokResult(res.data);
      toast.success('Analisi carta completata!');
      loadStoria();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore analisi carta');
    } finally { setAnalyzing(false); }
  };

  const deleteStoria = async (id) => {
    await api.delete(`/analisi/storia/${id}`);
    setStoria((prev) => (prev || []).filter((s) => s.id !== id));
    toast.success('Analisi eliminata');
  };

  const viewStoria = (item) => {
    if (item.risultato?.tipo === 'pokemon') {
      setPokResult(item.risultato);
      setResult(null);
    } else {
      setResult(item.risultato);
      setPokResult(null);
    }
    setSavedBanner(item.created_at);
  };

  return (
    <Layout>
      <div className="max-w-4xl mx-auto" style={{ paddingTop: 32 }}>
        <div style={{ marginBottom: 24 }}>
          <h1 className="page-title">Analisi Mercato</h1>
          <p className="section-subtitle">Scopri il valore del tuo prodotto</p>
        </div>

        {/* Banner consiglio */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', marginBottom: 20,
          borderRadius: 14, background: 'rgba(167,139,250,0.06)', border: `1px solid rgba(167,139,250,0.2)`,
        }}>
          <span style={{ fontSize: '1.3rem', flexShrink: 0 }}>💡</span>
          <p style={{ fontFamily: T.fontBody, fontSize: '0.82rem', color: T.text, margin: 0, lineHeight: 1.4 }}>
            <strong style={{ color: T.violet }}>Consiglio:</strong> Per un'analisi di mercato più affidabile, carica <strong>più foto possibili</strong> e inserisci un <strong>titolo accurato</strong> che descriva il prodotto nel dettaglio (brand, modello, colore, taglia).
          </p>
        </div>

        {fromProduct && analyzing && (
          <div style={{
            background:'rgba(124,58,237,0.1)', border:'1px solid #7c3aed',
            borderRadius:'12px', padding:'12px 16px', marginBottom:'20px',
            display:'flex', alignItems:'center', gap:'10px', flexWrap:'wrap',
            fontSize:'0.85rem', color:'#a78bfa'
          }}>
            🔗 Analisi avviata per: <strong style={{color:'#e2e8f0'}}>{prodottoState?.nome}</strong>
            {prodottoState?.foto_urls?.length > 0 && (
              <span style={{color:'#6b7280'}}>· {prodottoState?.foto_urls?.length || 0} foto disponibili per Claude Vision</span>
            )}
          </div>
        )}

        {!result && !pokResult && !analyzing && (
          <div className="card p-6 space-y-6">
            {prefilled && !fromProduct && (
              <div className="rounded-xl px-4 py-3 text-sm" style={{ backgroundColor: T.green + '10', border: `1px solid ${T.green}30`, color: T.green, fontFamily: T.fontBody }}>
                Prodotto pre-caricato dall'inventario ✅
              </div>
            )}
            {fromProduct && (
              <div style={{
                background:'rgba(124,58,237,0.1)', border:'1px solid #7c3aed',
                borderRadius:'12px', padding:'12px 16px',
                display:'flex', alignItems:'center', gap:'10px', flexWrap:'wrap',
                fontSize:'0.85rem', color:'#a78bfa'
              }}>
                🔗 Analisi per: <strong style={{color:'#e2e8f0'}}>{prodottoState?.nome}</strong>
                {prodottoState?.foto_urls?.length > 0 && (
                  <span style={{color:'#6b7280'}}>· {prodottoState?.foto_urls?.length || 0} foto</span>
                )}
              </div>
            )}

            <div>
              <label className="label">Categoria</label>
              <select value={categoria} onChange={(e) => setCategoria(e.target.value)} className="input">
                <option value="">Seleziona categoria...</option>
                {CATEGORIES.map((c) => <option key={c} value={c}>{c === 'Carte Pokemon' ? '🃏 Carte Pokemon' : c}</option>)}
              </select>
            </div>

            {categoria !== 'Carte Pokemon' && (
              <>
              <div>
                <p className="text-sm font-medium mb-3" style={{ color: T.text, fontFamily: T.fontBody }}>Modalita analisi</p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {[
                    { v: 'foto', label: '📸 Solo Foto' },
                    { v: 'nome', label: '✏️ Solo Nome' },
                    { v: 'foto_nome', label: '🔎 Foto + Nome', rec: true },
                  ].map((opt) => (
                    <button key={opt.v} onClick={() => setMode(opt.v)}
                      className="p-3 rounded-xl border text-sm font-medium transition-all"
                      style={{
                        borderColor: mode === opt.v ? T.borderActive : T.border,
                        backgroundColor: mode === opt.v ? T.borderActive + '10' : 'transparent',
                        color: mode === opt.v ? T.violet : T.muted,
                        fontFamily: T.fontBody,
                      }}>
                      {opt.label}
                      {opt.rec && <span className="block text-xs mt-0.5" style={{ color: T.muted }}>consigliato</span>}
                    </button>
                  ))}
                </div>
              </div>

              {mode !== 'foto' && (
                <div>
                  <label className="label">Nome prodotto</label>
                  <input type="text" value={nome} onChange={(e) => setNome(e.target.value)} className="input" placeholder="es. Nike Air Jordan 1 High OG" />
                </div>
              )}

              {mode !== 'nome' && (
                <div>
                  <label className="label">Foto (max 20)</label>
                  {previews.length === 0 ? (
                    <div onDrop={handleDrop} onDragOver={(e) => e.preventDefault()} onClick={() => fileRef.current?.click()}
                      className="border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors"
                      style={{ borderColor: T.border }}
                      onMouseEnter={(e) => e.currentTarget.style.borderColor = T.borderActive + '50'}
                      onMouseLeave={(e) => e.currentTarget.style.borderColor = T.border}>
                      <p className="text-sm" style={{ color: T.muted, fontFamily: T.fontBody }}>Trascina qui le foto o clicca per selezionare</p>
                      <p className="text-xs mt-1" style={{ color: T.faint }}>JPG, PNG, WebP</p>
                    </div>
                  ) : null}
                  <input ref={fileRef} type="file" accept="image/*" multiple onChange={handleFiles} className="hidden" />
                  {previews.length > 0 && (
                    <div className="flex flex-wrap gap-3 mt-3">
                      {previews.map((p, i) => (
                        <img key={i} src={p} alt="" className="h-20 w-20 rounded-lg object-cover" style={{ border: `1px solid ${T.border}` }} />
                      ))}
                      <button onClick={() => fileRef.current?.click()}
                        className="h-20 w-20 rounded-lg border-2 border-dashed flex items-center justify-center text-xl transition-colors"
                        style={{ borderColor: T.border, color: T.muted }}
                        onMouseEnter={(e) => { e.currentTarget.style.borderColor = T.borderActive + '50'; e.currentTarget.style.color = T.violet; }}
                        onMouseLeave={(e) => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.color = T.muted; }}>+</button>
                    </div>
                  )}
                </div>
              )}
              </>
            )}

            {/* Form Pokemon specializzato */}
            {categoria === 'Carte Pokemon' ? (
              <>
                <div>
                  <label className="label">Nome carta</label>
                  <input type="text" value={pokNome} onChange={(e) => setPokNome(e.target.value)} className="input" placeholder="es. Charizard, Pikachu, Squirtle" />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="label">Mercato</label>
                    <select value={pokMercato} onChange={(e) => { setPokMercato(e.target.value); if (e.target.value === 'occidentale') setPokLingua('EN'); else if (e.target.value === 'orientale') setPokLingua('JP'); }} className="input">
                      {POKEMON_MERCATI.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                    </select>
                  </div>
                  {pokMercato !== 'cinese_s' && (
                    <div>
                      <label className="label">Lingua</label>
                      <select value={pokLingua} onChange={(e) => setPokLingua(e.target.value)} className="input">
                        {(pokMercato === 'occidentale' ? LINGUE_OCCIDENTALE : LINGUE_ORIENTALE).map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
                      </select>
                    </div>
                  )}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="label">Codice Set</label>
                    <input type="text" value={pokCodiceSet} onChange={(e) => setPokCodiceSet(e.target.value)} className="input" placeholder="es. SCR, svG, PROMO" />
                  </div>
                  <div>
                    <label className="label">Numero carta</label>
                    <input type="text" value={pokNumero} onChange={(e) => setPokNumero(e.target.value)} className="input" placeholder="es. 148, 052, 099" />
                  </div>
                </div>
                {/* Carta gradata */}
                <div>
                  <label className="label" style={{ marginBottom: 8 }}>Carta Gradata?</label>
                  <div style={{ display: 'flex', gap: 8 }}>
                    {[{ v: false, label: 'No' }, { v: true, label: 'Sì' }].map(opt => (
                      <button key={String(opt.v)} onClick={() => setPokGradata(opt.v)}
                        style={{
                          flex: 1, padding: '8px 0', borderRadius: 10, fontSize: '0.85rem', fontWeight: 600,
                          fontFamily: T.fontBody, cursor: 'pointer', transition: '0.2s',
                          background: pokGradata === opt.v ? (opt.v ? 'rgba(74,222,128,0.15)' : T.elem) : 'transparent',
                          border: `1px solid ${pokGradata === opt.v ? (opt.v ? T.green : T.border) : T.border}`,
                          color: pokGradata === opt.v ? (opt.v ? T.green : T.text) : T.muted,
                        }}>
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Campi grading — solo se gradata */}
                {pokGradata && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" style={{ animation: 'fadeInUp 0.3s ease' }}>
                    <div>
                      <label className="label">Casa di Grading</label>
                      <select value={pokCasaGrading} onChange={(e) => setPokCasaGrading(e.target.value)} className="input">
                        {CASE_GRADING.map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="label">Voto</label>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <select value={pokVotoGrading} onChange={(e) => setPokVotoGrading(parseFloat(e.target.value))} className="input" style={{ flex: 1 }}>
                          {VOTI_GRADING.map(v => <option key={v} value={v}>{v}</option>)}
                        </select>
                        <span style={{ fontFamily: T.fontDisplay, fontSize: '1.8rem', fontWeight: 800, color: T.violet, minWidth: 50, textAlign: 'center' }}>
                          {pokVotoGrading}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                <div style={{ padding: '10px 14px', borderRadius: 10, background: 'rgba(167,139,250,0.06)', border: `1px solid rgba(167,139,250,0.15)` }}>
                  <p style={{ fontFamily: T.fontBody, fontSize: '0.75rem', color: T.muted, margin: 0, lineHeight: 1.5 }}>
                    💡 Trovi il codice set in basso a sinistra della carta nel rettangolo. Es: <strong style={{ color: T.text }}>SCR EN 148/142</strong> → Codice: SCR, Numero: 148, Lingua: EN
                  </p>
                </div>
                <button onClick={handleAnalyzePokemon} className="btn-primary w-full py-3.5 rounded-xl text-sm">
                  🔍 Analizza Carta
                </button>
              </>
            ) : (
              <>
                <button onClick={handleAnalyze} className="btn-primary w-full py-3.5 rounded-xl text-sm">
                  Avvia Analisi
                </button>
                <p style={{ fontSize: 12, color: '#9ca3af', marginTop: 8, textAlign: 'center', fontFamily: T.fontBody }}>
                  💡 I risultati sono validi per 24 ore. Se cerchi lo stesso prodotto te lo mostriamo subito senza consumare quota.
                </p>
                {quote?.serpapi_analisi && (
                  <p style={{ fontSize: 12, color: '#9ca3af', marginTop: 4, textAlign: 'center', fontFamily: T.fontBody }}>
                    Hai {quote.serpapi_analisi.rimanenti}/{quote.serpapi_analisi.limite} analisi rimanenti oggi
                  </p>
                )}
              </>
            )}
          </div>
        )}

        {analyzing && (
          <div className="rounded-2xl p-12 text-center" style={{ backgroundColor: T.card, border: `1px solid ${T.border}`, borderRadius: 16 }}>
            <div className="relative w-20 h-20 mx-auto mb-6">
              <div className="w-20 h-20 rounded-full animate-spin" style={{ background: `conic-gradient(${T.violetDark}, ${T.violetDeep}, ${T.violet}, ${T.violetDark})` }} />
              <div className="absolute inset-1 rounded-full flex items-center justify-center" style={{ backgroundColor: T.card }}>
                <span className="text-2xl">🤖</span>
              </div>
            </div>
            <p className="font-bold text-lg mb-2" style={{ color: T.text, fontFamily: T.fontBody }}>L'AI sta analizzando il mercato...</p>
            <p className="text-sm mb-4 transition-opacity" style={{ color: T.violet }}>{LOADING_TEXTS[loadingText]}</p>
            <div className="w-64 h-2 rounded-full mx-auto mb-4 overflow-hidden" style={{ backgroundColor: T.bg }}>
              <div className="h-full rounded-full" style={{ background: T.gradPrimary, animation: 'progressBar 20s ease-out forwards' }} />
            </div>
            <div className="flex gap-2 justify-center">
              {[0, 1, 2].map((i) => (
                <span key={i} className="w-2 h-2 rounded-full" style={{ backgroundColor: T.violet, animation: `pulse 1.2s ease-in-out ${i * 0.2}s infinite` }} />
              ))}
            </div>
          </div>
        )}

        {/* Risultati Pokemon */}
        {pokResult && !analyzing && (
          <div className="space-y-6">
            {savedBanner && (
              <div className="rounded-xl px-4 py-3 text-sm" style={{ backgroundColor: T.amber + '10', border: `1px solid ${T.amber}30`, color: T.amber, fontFamily: T.fontBody }}>
                📂 Analisi salvata del {new Date(savedBanner).toLocaleString('it-IT')} — non aggiornata in tempo reale
              </div>
            )}
            {/* Header carta */}
            <div className="card p-6">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
                <div>
                  <p style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.1em', color: T.muted, fontFamily: T.fontBody, marginBottom: 4 }}>Carta Pokemon</p>
                  <h2 style={{ fontFamily: T.fontDisplay, fontSize: '1.3rem', fontWeight: 800, color: T.text, margin: 0 }}>
                    {pokResult.carta?.nome} <span style={{ color: T.violet }}>{pokResult.carta?.set} {pokResult.carta?.numero}</span>
                  </h2>
                  <p style={{ fontSize: '0.8rem', color: T.muted, fontFamily: T.fontBody, marginTop: 4 }}>
                    {pokResult.carta?.lingua} · {pokResult.carta?.mercato}
                  </p>
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <span style={{
                    padding: '4px 12px', borderRadius: 20, fontSize: '0.72rem', fontWeight: 700, fontFamily: T.fontBody,
                    background: pokResult.carta?.mercato === 'Occidentale' ? 'rgba(34,211,238,0.12)' : pokResult.carta?.mercato === 'Orientale' ? 'rgba(248,113,113,0.12)' : 'rgba(251,191,36,0.12)',
                    color: pokResult.carta?.mercato === 'Occidentale' ? T.cyan : pokResult.carta?.mercato === 'Orientale' ? T.red : T.amber,
                    border: `1px solid ${pokResult.carta?.mercato === 'Occidentale' ? 'rgba(34,211,238,0.3)' : pokResult.carta?.mercato === 'Orientale' ? 'rgba(248,113,113,0.3)' : 'rgba(251,191,36,0.3)'}`,
                  }}>
                    {pokResult.carta?.mercato === 'Occidentale' ? '🌍' : pokResult.carta?.mercato === 'Orientale' ? '🌏' : '🇨🇳'} {pokResult.carta?.mercato}
                  </span>
                  {pokResult.cached && <span style={{ fontSize: '0.65rem', color: T.amber }}>⚡ cache</span>}
                </div>
              </div>
              {/* Badge condizione/grading */}
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
                {pokResult.carta?.gradata ? (
                  <span style={{
                    padding: '4px 12px', borderRadius: 20, fontSize: '0.75rem', fontWeight: 700, fontFamily: T.fontBody,
                    background: pokResult.carta?.casa_grading?.includes('PSA') ? 'rgba(248,113,113,0.15)' : pokResult.carta?.casa_grading?.includes('BGS') ? 'rgba(34,211,238,0.15)' : pokResult.carta?.casa_grading?.includes('GRAAD') ? 'rgba(167,139,250,0.15)' : 'rgba(251,191,36,0.15)',
                    color: pokResult.carta?.casa_grading?.includes('PSA') ? T.red : pokResult.carta?.casa_grading?.includes('BGS') ? T.cyan : pokResult.carta?.casa_grading?.includes('GRAAD') ? T.violet : T.amber,
                    border: `1px solid ${pokResult.carta?.casa_grading?.includes('PSA') ? 'rgba(248,113,113,0.3)' : pokResult.carta?.casa_grading?.includes('BGS') ? 'rgba(34,211,238,0.3)' : pokResult.carta?.casa_grading?.includes('GRAAD') ? 'rgba(167,139,250,0.3)' : 'rgba(251,191,36,0.3)'}`,
                  }}>
                    {pokResult.carta?.casa_grading} {pokResult.carta?.voto_grading}
                  </span>
                ) : (
                  <span style={{ padding: '4px 10px', borderRadius: 20, fontSize: '0.68rem', fontWeight: 600, fontFamily: T.fontBody, background: 'rgba(74,222,128,0.08)', border: '1px solid rgba(74,222,128,0.2)', color: T.green }}>
                    Escluse carte gradate
                  </span>
                )}
              </div>
            </div>

            {/* 3 Card prezzi */}
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: 16 }}>
              <div className="card" style={{ padding: 20, textAlign: 'center' }}>
                <p style={{ fontSize: '0.7rem', textTransform: 'uppercase', color: T.muted, fontFamily: T.fontBody, marginBottom: 8 }}>💚 Prezzo medio venduto</p>
                <p style={{ fontSize: '2rem', fontWeight: 800, color: T.green, fontFamily: T.fontBody }}>
                  {pokResult.ebay_venduto?.prezzo_medio > 0 ? `€${pokResult.ebay_venduto.prezzo_medio.toFixed(2)}` : 'N/D'}
                </p>
                <p style={{ fontSize: '0.72rem', color: T.muted }}>{pokResult.ebay_venduto?.num_vendite || 0} vendite trovate</p>
              </div>
              <div className="card" style={{ padding: 20, textAlign: 'center' }}>
                <p style={{ fontSize: '0.7rem', textTransform: 'uppercase', color: T.muted, fontFamily: T.fontBody, marginBottom: 8 }}>📊 Range prezzi</p>
                <p style={{ fontSize: '1.3rem', fontWeight: 700, color: T.text, fontFamily: T.fontBody }}>
                  {pokResult.ebay_venduto?.prezzo_min > 0 ? `€${pokResult.ebay_venduto.prezzo_min.toFixed(2)} — €${pokResult.ebay_venduto.prezzo_max.toFixed(2)}` : 'N/D'}
                </p>
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 6, marginTop: 6 }}>
                  <span style={{ fontSize: '0.78rem', fontWeight: 700, color: pokResult.ebay_venduto?.trend === 'crescita' ? T.green : pokResult.ebay_venduto?.trend === 'calo' ? T.red : T.amber }}>
                    {pokResult.ebay_venduto?.trend === 'crescita' ? '↑ In crescita' : pokResult.ebay_venduto?.trend === 'calo' ? '↓ In calo' : '→ Stabile'}
                  </span>
                </div>
              </div>
              <div className="card" style={{ padding: 20, textAlign: 'center' }}>
                <p style={{ fontSize: '0.7rem', textTransform: 'uppercase', color: T.muted, fontFamily: T.fontBody, marginBottom: 8 }}>🎯 Prezzo consigliato</p>
                <p style={{ fontSize: '2rem', fontWeight: 800, color: T.violet, fontFamily: T.fontBody }}>
                  {pokResult.prezzo_consigliato > 0 ? `€${pokResult.prezzo_consigliato.toFixed(2)}` : 'N/D'}
                </p>
                <p style={{ fontSize: '0.72rem', color: T.muted }}>media venduto -5%</p>
              </div>
            </div>

            {/* Venduti recenti */}
            {pokResult.ebay_venduto?.vendite_recenti?.length > 0 && (
              <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
                <div style={{ padding: '14px 20px', borderBottom: `1px solid ${T.border}` }}>
                  <h3 style={{ fontFamily: T.fontDisplay, fontSize: '0.95rem', fontWeight: 700, color: T.text, margin: 0 }}>Ultimi venduti su eBay</h3>
                </div>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ background: T.elem }}>
                        <th style={{ padding: '8px 16px', textAlign: 'left', fontSize: '0.7rem', textTransform: 'uppercase', color: T.muted, fontFamily: T.fontBody }}>Titolo</th>
                        <th style={{ padding: '8px 16px', textAlign: 'right', fontSize: '0.7rem', textTransform: 'uppercase', color: T.muted, fontFamily: T.fontBody }}>Prezzo</th>
                        <th style={{ padding: '8px 16px', textAlign: 'center', fontSize: '0.7rem', textTransform: 'uppercase', color: T.muted, fontFamily: T.fontBody }}>Link</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pokResult.ebay_venduto.vendite_recenti.map((v, i) => (
                        <tr key={i} style={{ borderBottom: `1px solid rgba(45,31,110,0.3)` }}>
                          <td style={{ padding: '8px 16px', fontSize: '0.8rem', color: T.text, fontFamily: T.fontBody, maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v.titolo}</td>
                          <td style={{ padding: '8px 16px', textAlign: 'right', fontSize: '0.85rem', fontWeight: 700, color: T.green, fontFamily: T.fontBody }}>€{v.prezzo.toFixed(2)}</td>
                          <td style={{ padding: '8px 16px', textAlign: 'center' }}>
                            {v.link && <a href={v.link} target="_blank" rel="noopener noreferrer" style={{ color: T.violet, fontSize: '0.75rem', textDecoration: 'none' }}>Vedi →</a>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Annunci attivi */}
            {pokResult.ebay_attivo?.annunci?.length > 0 && (
              <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
                <div style={{ padding: '14px 20px', borderBottom: `1px solid ${T.border}` }}>
                  <h3 style={{ fontFamily: T.fontDisplay, fontSize: '0.95rem', fontWeight: 700, color: T.text, margin: 0 }}>
                    In vendita ora <span style={{ fontSize: '0.75rem', color: T.muted, fontWeight: 400 }}>({pokResult.ebay_attivo.num_annunci} annunci)</span>
                  </h3>
                </div>
                {pokResult.ebay_attivo.annunci.map((a, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 20px', borderBottom: `1px solid rgba(45,31,110,0.2)` }}>
                    {a.foto && <img src={a.foto} alt="" style={{ width: 40, height: 40, borderRadius: 8, objectFit: 'cover', flexShrink: 0 }} />}
                    <p style={{ flex: 1, fontSize: '0.8rem', color: T.text, fontFamily: T.fontBody, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', margin: 0 }}>{a.titolo}</p>
                    <span style={{ fontWeight: 700, color: T.violet, fontSize: '0.9rem', fontFamily: T.fontBody, flexShrink: 0 }}>€{a.prezzo.toFixed(2)}</span>
                    {a.link && <a href={a.link} target="_blank" rel="noopener noreferrer" style={{ color: T.muted, fontSize: '0.72rem', textDecoration: 'none', flexShrink: 0 }}>Vedi →</a>}
                  </div>
                ))}
              </div>
            )}

            {/* Avviso filtri */}
            <p style={{ fontSize: '0.72rem', color: T.faint, fontFamily: T.fontBody, textAlign: 'center' }}>
              Risultati filtrati — esclusi lotti e bundle. Basato su {pokResult.ebay_venduto?.num_vendite || 0} vendite recenti.
            </p>

            {/* Bottone nuova analisi */}
            <button onClick={() => { setPokResult(null); setSavedBanner(null); }} className="btn-secondary w-full py-3 rounded-xl text-sm">
              🔍 Nuova analisi
            </button>
          </div>
        )}

        {result && (
          <div className="space-y-6">
            {savedBanner && (
              <div className="rounded-xl px-4 py-3 text-sm" style={{ backgroundColor: T.amber + '10', border: `1px solid ${T.amber}30`, color: T.amber, fontFamily: T.fontBody }}>
                📂 Analisi salvata del {new Date(savedBanner).toLocaleString('it-IT')} — non aggiornata in tempo reale
              </div>
            )}
            {result._from_cache && (
              <div style={{
                display:'flex', alignItems:'center', gap:'8px',
                background:'rgba(251,191,36,0.08)', border:'1px solid rgba(251,191,36,0.2)',
                borderRadius:'9999px', padding:'6px 14px', marginBottom:'0',
                fontSize:'0.78rem', color:'#fbbf24', fontFamily:'DM Sans'
              }}>
                ⚡ Risultato dalla cache · aggiornato {result._cache_age_minutes} minuti fa ·{' '}
                <span style={{color:'#6b7280'}}>prossimo aggiornamento tra {360 - result._cache_age_minutes} min</span>
              </div>
            )}
            <div className="card p-6">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                  <p className="text-xs uppercase tracking-wide mb-1" style={{ color: T.muted, fontFamily: T.fontBody }}>Prodotto identificato</p>
                  <h2 className="font-bold text-xl" style={{ color: T.text, fontFamily: T.fontDisplay }}>{result.prodotto}</h2>
                </div>
                <ScoreBadge score={result.score} />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <PriceCard title="eBay Venduto" data={result.prezzi?.ebay_venduto} color={T.cyan} />
              <PriceCard title="eBay Attivo" data={result.prezzi?.ebay_attivo} color={T.violet} />
              <PriceCard title="Vinted" data={result.prezzi?.vinted} color={T.green} />
              {result.prezzi?.stockx && <PriceCard title="StockX" data={result.prezzi.stockx} color={T.green} />}
            </div>

            {result.prezzo_consigliato && (
              <div className="rounded-2xl p-6" style={{ backgroundColor: T.violetDark + '08', border: `1px solid ${T.violetDark}30` }}>
                <h3 className="font-bold text-sm mb-4 uppercase tracking-wide" style={{ color: T.violet, fontFamily: T.fontBody }}>Prezzo Consigliato</h3>
                <div className="grid grid-cols-3 gap-4" style={{ fontFamily: T.fontBody }}>
                  <div className="text-center"><p className="text-xs mb-1" style={{ color: T.muted }}>Vendita veloce</p><p className="text-xl font-bold" style={{ color: T.text }}>{'\u20AC'}{result.prezzo_consigliato.veloce?.toFixed(2)}</p></div>
                  <div className="text-center"><p className="text-xs mb-1 font-medium" style={{ color: T.violet }}>Sweet spot</p><p className="text-2xl font-black" style={{ color: T.violet }}>{'\u20AC'}{result.prezzo_consigliato.sweet_spot?.toFixed(2)}</p></div>
                  <div className="text-center"><p className="text-xs mb-1" style={{ color: T.muted }}>Massimo</p><p className="text-xl font-bold" style={{ color: T.text }}>{'\u20AC'}{result.prezzo_consigliato.massimo?.toFixed(2)}</p></div>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="card p-4">
                <p className="text-xs uppercase mb-1" style={{ color: T.muted, fontFamily: T.fontBody }}>Domanda</p>
                <p className="text-lg font-bold" style={{ color: result.domanda === 'Alta' ? T.green : result.domanda === 'Bassa' ? T.red : T.amber, fontFamily: T.fontBody }}>{result.domanda}</p>
              </div>
              <div className="card p-4">
                <p className="text-xs uppercase mb-1" style={{ color: T.muted, fontFamily: T.fontBody }}>Velocita vendita</p>
                <p className="text-lg font-bold" style={{ color: T.text, fontFamily: T.fontBody }}>{result.velocita_vendita}</p>
              </div>
              <div className="card p-4">
                <p className="text-xs uppercase mb-1" style={{ color: T.muted, fontFamily: T.fontBody }}>Concorrenza</p>
                <p className="text-lg font-bold" style={{ color: result.concorrenza === 'Alta' ? T.red : result.concorrenza === 'Bassa' ? T.green : T.amber, fontFamily: T.fontBody }}>{result.concorrenza}</p>
              </div>
            </div>

            {result.consigli && (
              <div className="card p-6">
                <h3 className="font-bold text-sm mb-3" style={{ color: T.text, fontFamily: T.fontDisplay }}>Consigli</h3>
                <p className="text-sm leading-relaxed whitespace-pre-wrap" style={{ color: T.text, fontFamily: T.fontBody, opacity: 0.85 }}>{result.consigli}</p>
              </div>
            )}

            {result.fonti && (
              <div style={{marginTop:'16px', padding:'14px', background:'#1c1640', borderRadius:'14px', border:'1px solid #2d1b69'}}>
                <p style={{fontSize:'0.75rem', color:'#6b7280', marginBottom:'8px', textTransform:'uppercase', letterSpacing:'0.05em', fontFamily: T.fontBody}}>
                  Fonti dati
                </p>
                <div style={{display:'flex', flexWrap:'wrap', gap:'8px'}}>
                  {[
                    {nome:'eBay Venduto', ok: result.fonti?.ebay_venduti === 'disponibile'},
                    {nome:'eBay Attivo', ok: result.fonti?.ebay_attivi === 'disponibile'},
                    {nome:'Google Shopping', ok: result.fonti?.google_shopping === 'disponibile'},
                    {nome:'Vinted', ok: result.fonti?.vinted === 'disponibile'},
                    {nome:'Claude Vision', ok: result.fonti?.google_lens === 'disponibile'},
                  ].map(f => (
                    <span key={f.nome} style={{
                      padding:'3px 10px', borderRadius:'9999px', fontSize:'0.72rem', fontWeight:600,
                      background: f.ok ? 'rgba(74,222,128,0.1)' : 'rgba(248,113,113,0.1)',
                      border: `1px solid ${f.ok ? '#4ade80' : '#f87171'}`,
                      color: f.ok ? '#4ade80' : '#f87171'
                    }}>
                      {f.ok ? '✓' : '✗'} {f.nome}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <button onClick={() => { setResult(null); setFiles([]); setPreviews([]); setPrefilled(false); setSavedBanner(null); }}
              className="btn-secondary w-full py-3 rounded-xl text-sm">
              Nuova Analisi
            </button>
          </div>
        )}

        {/* Cronologia */}
        {!analyzing && (() => {
          const totalStoria = (storia || []).length;
          const totalPages = Math.max(1, Math.ceil(totalStoria / STORIA_PER_PAGE));
          const currentPage = Math.min(storiaPage, totalPages);
          const paginatedStoria = (storia || []).slice((currentPage - 1) * STORIA_PER_PAGE, currentPage * STORIA_PER_PAGE);
          return (
          <div className="mt-10">
            <div className="flex items-center gap-2 mb-4">
              <h2 className="section-title" style={{ fontFamily: T.fontDisplay, color: T.text }}>📜 Analisi precedenti</h2>
              {totalStoria > 0 && <span className="badge badge-violet">{totalStoria}</span>}
            </div>
            {totalStoria === 0 ? (
              <p className="text-sm" style={{ color: T.muted, fontFamily: T.fontBody }}>Nessuna analisi salvata ancora. Fai la tua prima analisi!</p>
            ) : (
              <>
              <div className="space-y-2">
                {paginatedStoria.map((s) => {
                  const sc = s.score || 0;
                  const scoreColor = sc >= 8 ? T.green : sc >= 4 ? T.amber : T.red;
                  const sweet = s.risultato?.prezzo_consigliato?.sweet_spot;
                  return (
                    <div key={s.id} style={{
                      display: 'flex', alignItems: 'center', gap: '8px',
                      padding: isMobile ? '10px 12px' : '12px 16px',
                      background: '#151230', border: '1px solid #2d1b69',
                      borderRadius: 12, marginBottom: 8, overflow: 'hidden',
                    }}
                      onMouseEnter={(e) => e.currentTarget.style.borderColor = T.borderActive}
                      onMouseLeave={(e) => e.currentTarget.style.borderColor = '#2d1b69'}>
                      {/* Thumbnail */}
                      {s.foto_url ? (
                        <img src={`${API_BASE}${s.foto_url}`} alt="" style={{ width: 44, height: 44, borderRadius: 8, objectFit: 'cover', flexShrink: 0, border: `1px solid ${T.border}` }}
                          onError={(e) => { e.target.style.display = 'none'; const fb = document.createElement('div'); fb.style.cssText = 'width:44px;height:44px;border-radius:8px;flex-shrink:0;display:flex;align-items:center;justify-content:center;background:#1c1640'; fb.innerHTML = '<span style="font-size:1rem">📦</span>'; e.target.parentElement.insertBefore(fb, e.target); }} />
                      ) : (
                        <div style={{ width: 44, height: 44, borderRadius: 8, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: T.elem }}><span style={{ fontSize: '1rem' }}>📦</span></div>
                      )}
                      {/* Score badge */}
                      <div style={{ width: 28, height: 28, borderRadius: 6, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem', fontWeight: 800, backgroundColor: scoreColor + '20', color: scoreColor }}>{sc}</div>
                      {/* Info centrale */}
                      <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                        <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#e2e8f0', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontFamily: T.fontBody }}>{s.prodotto}</div>
                        <div style={{ fontSize: '0.65rem', color: '#6b7280' }}>{new Date(s.created_at).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</div>
                        {sweet && <div style={{ fontSize: '0.78rem', fontWeight: 700, color: '#4ade80' }}>{'\u20AC'}{(sweet || 0).toFixed(2)}</div>}
                      </div>
                      {/* Bottoni azione */}
                      <div style={{ display: 'flex', gap: '4px', flexShrink: 0 }}>
                        <button onClick={() => viewStoria(s)} style={{ fontSize: '0.7rem', padding: '4px 8px', borderRadius: 9999, background: T.elem, border: `1px solid ${T.border}`, color: T.text, cursor: 'pointer', fontFamily: T.fontBody }}>Rivedi</button>
                        <button onClick={() => deleteStoria(s.id)} style={{ width: 28, height: 28, borderRadius: '50%', background: 'rgba(248,113,113,0.15)', border: '1px solid #f87171', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: '0.75rem' }}>🗑</button>
                      </div>
                    </div>
                  );
                })}
              </div>
              {/* Paginazione */}
              {totalPages > 1 && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginTop: 16 }}>
                  <button onClick={() => setStoriaPage(p => Math.max(1, p - 1))} disabled={currentPage <= 1}
                    style={{ width: 36, height: 36, borderRadius: 10, background: currentPage <= 1 ? T.elem : 'rgba(167,139,250,0.1)', border: `1px solid ${currentPage <= 1 ? T.border : T.violetDark}`, color: currentPage <= 1 ? T.faint : T.violet, cursor: currentPage <= 1 ? 'default' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1rem', fontWeight: 700 }}>
                    ←
                  </button>
                  <span style={{ fontFamily: T.fontBody, fontSize: '0.8rem', color: T.muted, padding: '0 8px' }}>
                    {currentPage} / {totalPages}
                  </span>
                  <button onClick={() => setStoriaPage(p => Math.min(totalPages, p + 1))} disabled={currentPage >= totalPages}
                    style={{ width: 36, height: 36, borderRadius: 10, background: currentPage >= totalPages ? T.elem : 'rgba(167,139,250,0.1)', border: `1px solid ${currentPage >= totalPages ? T.border : T.violetDark}`, color: currentPage >= totalPages ? T.faint : T.violet, cursor: currentPage >= totalPages ? 'default' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1rem', fontWeight: 700 }}>
                    →
                  </button>
                </div>
              )}
              </>
            )}
          </div>
          );
        })()}
      </div>
    </Layout>
  );
}
